const n = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v)) ? null : Number(v));

// Points table: [[minValue, points], ...] in descending order; the first row whose min <= value wins.
const band = (v, table) => {
  for (const [min, pts] of table) if (v >= min) return pts;
  return table[table.length - 1][1];
};
const sum = (vals) => (vals.length ? vals.reduce((a, b) => a + b, 0) : null);
const LOW = -Infinity;

export const calcMAP = (sbp, dbp) => (sbp != null && dbp != null ? Math.round((sbp + 2 * dbp) / 3) : null);

const T = {
  sofaPF: [[400, 0], [300, 1], [200, 2], [100, 3], [LOW, 4]],
  sofaPlt: [[150, 0], [100, 1], [50, 2], [20, 3], [LOW, 4]],
  sofaBili: [[12, 4], [6, 3], [2, 2], [1.2, 1], [LOW, 0]],
  sofaGCS: [[15, 0], [13, 1], [10, 2], [6, 3], [LOW, 4]],
  sofaCr: [[5, 4], [3.5, 3], [2, 2], [1.2, 1], [LOW, 0]],
  sofaUO: [[500, 0], [200, 3], [LOW, 4]],
  apTemp: [[41, 4], [39, 3], [38.5, 1], [36, 0], [34, 1], [32, 2], [30, 3], [LOW, 4]],
  apMAP: [[160, 4], [130, 3], [110, 2], [70, 0], [50, 2], [LOW, 4]],
  apHR: [[180, 4], [140, 3], [110, 2], [70, 0], [55, 2], [40, 3], [LOW, 4]],
  apRR: [[50, 4], [35, 3], [25, 1], [12, 0], [10, 1], [6, 2], [LOW, 4]],
  apAa: [[500, 4], [350, 3], [200, 2], [LOW, 0]],
  apPaO2: [[61, 1], [55, 3], [LOW, 4]],
  apPH: [[7.7, 4], [7.6, 3], [7.5, 1], [7.33, 0], [7.25, 2], [7.15, 3], [LOW, 4]],
  apNa: [[180, 4], [160, 3], [155, 2], [150, 1], [130, 0], [120, 2], [111, 3], [LOW, 4]],
  apK: [[7, 4], [6, 3], [5.5, 1], [3.5, 0], [3, 1], [2.5, 2], [LOW, 4]],
  apCr: [[3.5, 4], [2, 3], [1.5, 2], [0.6, 0], [LOW, 2]],
  apHct: [[60, 4], [50, 2], [46, 1], [30, 0], [20, 2], [LOW, 4]],
  apWBC: [[40, 4], [20, 2], [15, 1], [3, 0], [1, 2], [LOW, 4]],
  apAge: [[75, 6], [65, 5], [55, 3], [45, 2], [LOW, 0]],
  apMort: [[35, ">85%"], [30, "~75%"], [25, "~55%"], [20, "~40%"], [15, "~25%"], [10, "~15%"], [5, "~8%"], [LOW, "~4%"]],
};

function readInputs(vitals, labs, age) {
  const e = n(vitals.gcs_e), v = n(vitals.gcs_v), m = n(vitals.gcs_m);
  const sbp = n(vitals.sbp), dbp = n(vitals.dbp);
  const fio2p = n(labs.fio2);
  return {
    hr: n(vitals.hr), sbp, rr: n(vitals.rr), temp: n(vitals.temp), ht: n(vitals.height), wt: n(vitals.weight),
    gcs: e && v && m ? e + v + m : null, map: calcMAP(sbp, dbp),
    pao2: n(labs.pao2), fio2: fio2p ? fio2p / 100 : null, paco2: n(labs.paco2),
    plt: n(labs.platelets), bili: n(labs.bilirubin), cr: n(labs.creatinine), uo: n(labs.urine_output),
    ph: n(labs.ph), na: n(labs.na), k: n(labs.k), hct: n(labs.hct), wbc: n(labs.wbc), age: n(age),
    vaso: n(labs.vasopressor) || 0, vent: !!labs.ventilated, arf: !!labs.arf,
    chronic: !!labs.chronic_health, elective: labs.admission_type === "elective",
  };
}

function calcQSOFA({ rr, sbp, gcs }) {
  const criteria = { rr_ge_22: rr != null && rr >= 22, sbp_le_100: sbp != null && sbp <= 100, altered_mentation: gcs != null && gcs < 15 };
  const any = rr != null || sbp != null || gcs != null;
  return { qsofa: any ? Object.values(criteria).filter(Boolean).length : null, qsofa_criteria: criteria };
}

function sofaRespiration(pf, vent) {
  if (pf == null) return null;
  const pts = band(pf, T.sofaPF);
  return pts >= 3 && !vent ? 2 : pts;
}

function sofaRenal(cr, uo) {
  if (cr == null && uo == null) return null;
  return Math.max(cr == null ? 0 : band(cr, T.sofaCr), uo == null ? 0 : band(uo, T.sofaUO));
}

function sofaCardio(map, vaso) {
  if (map == null && !vaso) return null;
  return Math.max(vaso, map != null && map < 70 ? 1 : 0);
}

function calcSOFA(x, pf) {
  const opt = (val, table) => (val == null ? null : band(val, table));
  const c = {
    respiration: sofaRespiration(pf, x.vent),
    coagulation: opt(x.plt, T.sofaPlt),
    liver: opt(x.bili, T.sofaBili),
    cardiovascular: sofaCardio(x.map, x.vaso),
    cns: opt(x.gcs, T.sofaGCS),
    renal: sofaRenal(x.cr, x.uo),
  };
  const vals = Object.values(c).filter((v) => v != null);
  return { sofa: sum(vals), sofa_components: c, sofa_entered: `${vals.length}/6` };
}

function apacheOxygenation({ pao2, fio2, paco2 }) {
  if (pao2 == null || fio2 == null) return undefined;
  if (fio2 >= 0.5) return band(fio2 * 713 - (paco2 ?? 40) / 0.8 - pao2, T.apAa);
  return pao2 > 70 ? 0 : band(pao2, T.apPaO2);
}

function calcAPACHE(x) {
  const ap = {};
  const add = (key, val, table, mult = 1) => { if (val != null) ap[key] = band(val, table) * mult; };
  add("temperature", x.temp, T.apTemp);
  add("map", x.map, T.apMAP);
  add("heart_rate", x.hr, T.apHR);
  add("resp_rate", x.rr, T.apRR);
  const oxy = apacheOxygenation(x);
  if (oxy !== undefined) ap.oxygenation = oxy;
  add("ph", x.ph, T.apPH);
  add("sodium", x.na, T.apNa);
  add("potassium", x.k, T.apK);
  add("creatinine", x.cr, T.apCr, x.arf ? 2 : 1);
  add("hematocrit", x.hct, T.apHct);
  add("wbc", x.wbc, T.apWBC);
  if (x.gcs != null) ap.gcs = 15 - x.gcs;
  add("age", x.age, T.apAge);
  if (x.chronic) ap.chronic_health = x.elective ? 2 : 5;
  const vals = Object.values(ap);
  const total = sum(vals);
  return {
    apache2: total, apache2_components: ap, apache2_entered: `${vals.length}/14`,
    apache2_mortality: total == null ? null : band(total, T.apMort),
  };
}

export function computeScores(vitals = {}, labs = {}, age) {
  const x = readInputs(vitals, labs, age);
  const pf = x.pao2 && x.fio2 ? Math.round(x.pao2 / x.fio2) : null;
  return {
    map: x.map,
    bmi: x.ht && x.wt ? +(x.wt / (x.ht / 100) ** 2).toFixed(1) : null,
    gcs: x.gcs,
    shock_index: x.hr && x.sbp ? +(x.hr / x.sbp).toFixed(2) : null,
    modified_shock_index: x.hr && x.map ? +(x.hr / x.map).toFixed(2) : null,
    pf_ratio: pf,
    ...calcQSOFA(x),
    ...calcSOFA(x, pf),
    ...calcAPACHE(x),
  };
}

const level = (v, critical, warn) => {
  if (v == null) return null;
  if (critical(v)) return "critical";
  if (warn(v)) return "warn";
  return "ok";
};

export const severity = {
  gcs: (g) => level(g, (v) => v <= 8, (v) => v <= 12),
  qsofa: (q) => level(q, (v) => v >= 2, (v) => v === 1),
  sofa: (s) => level(s, (v) => v >= 10, (v) => v >= 2),
  apache2: (a) => level(a, (v) => v >= 25, (v) => v >= 15),
  shock_index: (s) => level(s, (v) => v >= 1, (v) => v >= 0.7),
};
